import React, { useRef, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./HomeTrendingSection.css";
import { useCurrency } from "../../Context/CurrencyContext";
import { useCart } from "../../Context/CartContext";
import { BsBagDash } from "react-icons/bs";

const HomeTrendingSection = () => {
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();

  // ─── Cart context ─────────────────────────────────────────────────────────
  const cartContext = useCart();
  const addToCart = cartContext?.addToCart || (() => {});
  const openCart = cartContext?.openCart || (() => {});

  const originalVideos = [
    {
      id: 3,
      src: "/videos/bestscreencare1.mp4",
      brand: "BKIND",
      title: "Algae Peel-Off Mask",
      price: 860,
      priceValue: 860,
      image:
        "https://wonder-theme-beauty.myshopify.com/cdn/shop/products/bkind-floral-face-2_718634ed-72d4-47b3-98aa-ab26bbc66519.jpg",
    },
    {
      id: 1,
      src: "/videos/bestscreencare2.mp4",
      brand: "BKIND",
      title: "Vitamin C Serum",
      price: 890,
      priceValue: 890,
      image:
        "https://wonder-theme-beauty.myshopify.com/cdn/shop/products/bkind-floral-face-2_718634ed-72d4-47b3-98aa-ab26bbc66519.jpg",
    },
    {
      id: 9,
      src: "/videos/bestscreencare3.mp4",
      brand: "BKIND",
      title: "Moisturizer Lotion",
      price: 720,
      priceValue: 720,
      image:
        "https://wonder-theme-beauty.myshopify.com/cdn/shop/products/bkind-floral-face-2_718634ed-72d4-47b3-98aa-ab26bbc66519.jpg",
    },
  ];

  const videos = [...originalVideos, ...originalVideos, ...originalVideos];

  const videoRefs = useRef([]);
  const gridRef = useRef(null);
  const carouselRef = useRef(null);
  const [cardWidth, setCardWidth] = useState(417);
  const [activeIndex, setActiveIndex] = useState(originalVideos.length);
  const [muted, setMuted] = useState(true);
  const [hovered, setHovered] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const transitionTimeoutRef = useRef(null);
  const initializedRef = useRef(false);

  // ─── Quick-view drawer state ──────────────────────────────────────────────
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerItem, setDrawerItem] = useState(null);
  const [drawerQty, setDrawerQty] = useState(1);

  // ─── Arrow visibility logic ───────────────────────────────────────────────
  // currentPosition = 0 → first video, originalVideos.length-1 → last video
  const currentPosition = activeIndex % originalVideos.length;
  const isFirst = currentPosition === 0;
  const isLast = currentPosition === originalVideos.length - 1;

  // On hover:
  //   first video  → only RIGHT arrow
  //   last video   → only LEFT arrow
  //   middle       → BOTH arrows
  const showLeft = hovered && !isFirst;
  const showRight = hovered && !isLast;

  // ─── Card Width Calculation ───────────────────────────────────────────────
  const calculateCardWidth = useCallback(() => {
    if (typeof window === "undefined") return 417;
    const screenWidth = window.innerWidth;
    if (screenWidth < 640) return screenWidth - 80;
    if (screenWidth < 1024) return 350;
    return 417;
  }, []);

  useEffect(() => {
    const updateCardWidth = () => setCardWidth(calculateCardWidth());
    updateCardWidth();
    window.addEventListener("resize", updateCardWidth);
    return () => window.removeEventListener("resize", updateCardWidth);
  }, [calculateCardWidth]);

  // ─── Center a card by index ───────────────────────────────────────────────
  const centerCard = useCallback((index) => {
    if (!gridRef.current || !carouselRef.current) return;
    const grid = gridRef.current;
    const card = grid.children[index];
    if (!card) return;
    const containerCenter = carouselRef.current.offsetWidth / 2;
    const cardCenter = card.offsetLeft + card.offsetWidth / 2;
    const translateX = cardCenter - containerCenter;
    grid.style.transition = "transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)";
    grid.style.transform = `translateX(-${translateX}px)`;
  }, []);

  // ─── Play video at index ──────────────────────────────────────────────────
  const playVideoAt = useCallback(
    (index, forceMuted = true) => {
      videoRefs.current.forEach((v) => {
        if (v) {
          v.pause();
          v.currentTime = 0;
        }
      });
      const video = videoRefs.current[index];
      if (!video) return;
      video.loop = true;
      video.muted = forceMuted ? true : muted;
      const tryPlay = () => {
        const promise = video.play();
        if (promise !== undefined) {
          promise.catch(() => {
            video.muted = true;
            video.play().catch(() => {});
          });
        }
      };
      if (video.readyState >= 2) {
        tryPlay();
      } else {
        video.load();
        video.addEventListener("canplay", tryPlay, { once: true });
      }
      centerCard(index);
      setActiveIndex(index);
    },
    [muted, centerCard]
  );

  // ─── Initial setup ────────────────────────────────────────────────────────
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    const startIndex = originalVideos.length;
    if (gridRef.current && carouselRef.current) {
      gridRef.current.style.transition = "none";
      const grid = gridRef.current;
      requestAnimationFrame(() => {
        const card = grid.children[startIndex];
        if (card) {
          const containerCenter = carouselRef.current.offsetWidth / 2;
          const cardCenter = card.offsetLeft + card.offsetWidth / 2;
          grid.style.transform = `translateX(-${cardCenter - containerCenter}px)`;
        }
        setTimeout(() => {
          playVideoAt(startIndex, true);
        }, 50);
      });
    }
  }, [originalVideos.length, playVideoAt]);

  // ─── Navigate carousel ────────────────────────────────────────────────────
  const navigateCarousel = useCallback(
    (direction) => {
      if (isTransitioning) return;
      setIsTransitioning(true);
      if (transitionTimeoutRef.current) clearTimeout(transitionTimeoutRef.current);

      let nextIndex = direction === "left" ? activeIndex - 1 : activeIndex + 1;
      if (nextIndex < 0) nextIndex = videos.length - 1;
      if (nextIndex >= videos.length) nextIndex = 0;

      playVideoAt(nextIndex, true);

      transitionTimeoutRef.current = setTimeout(() => {
        setIsTransitioning(false);
        setActiveIndex((prev) => {
          let corrected = prev;
          if (prev < originalVideos.length) {
            corrected = prev + originalVideos.length;
          } else if (prev >= originalVideos.length * 2) {
            corrected = prev - originalVideos.length;
          }
          if (corrected !== prev && gridRef.current && carouselRef.current) {
            gridRef.current.style.transition = "none";
            const card = gridRef.current.children[corrected];
            if (card) {
              const containerCenter = carouselRef.current.offsetWidth / 2;
              const cardCenter = card.offsetLeft + card.offsetWidth / 2;
              gridRef.current.style.transform = `translateX(-${cardCenter - containerCenter}px)`;
            }
          }
          return corrected;
        });
      }, 400);
    },
    [activeIndex, isTransitioning, videos.length, originalVideos.length, playVideoAt]
  );

  // ─── Mute toggle ──────────────────────────────────────────────────────────
  const toggleMute = (e) => {
    e.stopPropagation();
    const v = videoRefs.current[activeIndex];
    if (v) {
      const newMuted = !v.muted;
      v.muted = newMuted;
      setMuted(newMuted);
    }
  };

  // ─── Click handlers ───────────────────────────────────────────────────────
  const handleVideoClick = (index) => {
    if (index === activeIndex) return;
    playVideoAt(index, true);
  };

  const handleProductClick = (e, itemId) => {
    e.stopPropagation();
    navigate(`/product/${itemId}`);
  };

  const handleCartClick = (e, item) => {
    e.stopPropagation();
    setDrawerItem(item);
    setDrawerQty(1);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setDrawerItem(null);
    setDrawerQty(1);
  };

  const handleQtyDecrease = (e) => {
    e.stopPropagation();
    setDrawerQty((prev) => Math.max(1, prev - 1));
  };

  const handleQtyIncrease = (e) => {
    e.stopPropagation();
    setDrawerQty((prev) => prev + 1);
  };

  const handleAddToCart = (e) => {
    e.stopPropagation();
    if (!drawerItem) return;
    const cartItem = {
      uniqueId: `trending-${drawerItem.id}-${Date.now()}`,
      id: drawerItem.id,
      productId: drawerItem.id,
      name: drawerItem.title,
      title: drawerItem.title,
      price: drawerItem.priceValue,
      priceValue: drawerItem.priceValue,
      basePrice: drawerItem.priceValue,
      baseCurrency: "USD",
      qty: drawerQty,
      image: drawerItem.image,
      img: drawerItem.image,
      img1: drawerItem.image,
      brand: drawerItem.brand,
    };
    if (typeof addToCart === "function") addToCart(cartItem);
    handleDrawerClose();
    if (typeof openCart === "function") openCart();
  };

  const handleViewDetails = (e) => {
    e.stopPropagation();
    if (drawerItem) {
      handleDrawerClose();
      navigate(`/product/${drawerItem.id}`);
    }
  };

  const handleDotClick = (e, dotIndex) => {
    e.stopPropagation();
    const targetIndex = dotIndex + originalVideos.length;
    playVideoAt(targetIndex, true);
  };

  // ─── Volume Icon (with no black border) ───────────────────────────────────
  const VolumeIcon = ({ muted }) => {
    if (!muted) {
      return (
        <svg
          className="svg-icon svg-icon--volume"
          aria-hidden="true"
          focusable="false"
          role="presentation"
          xmlns="http://www.w3.org/2000/svg"
          fill="currentColor"
          width="20"
          height="20"
          viewBox="0 0 480 480"
          style={{ outline: "none", stroke: "none" }}
        >
          <g>
            <path d="M278.944,17.577c-5.568-2.656-12.128-1.952-16.928,1.92L106.368,144.009H32c-17.632,0-32,14.368-32,32v128c0,17.632,14.368,32,32,32h74.368l155.616,124.512c2.912,2.304,6.464,3.488,10.016,3.488c2.368,0,4.736-0.512,6.944-1.568c5.536-2.688,9.056-8.288,9.056-14.432v-416C288,25.865,284.48,20.265,278.944,17.577z M96,304.009H32v-128h64V304.009z M256,414.697l-128-102.4V167.71l128-102.4V414.697z" />
          </g>
          <g>
            <path d="M369.024,126.857c-6.304-6.24-16.416-6.144-22.624,0.128c-6.208,6.304-6.144,16.416,0.128,22.624c24.16,23.904,37.472,56,37.472,90.4c0,34.4-13.312,66.496-37.472,90.4c-6.304,6.208-6.368,16.32-0.128,22.624c3.136,3.168,7.264,4.736,11.36,4.736c4.064,0,8.128-1.536,11.264-4.64c30.304-29.92,46.976-70.08,46.976-113.12C416,196.969,399.328,156.809,369.024,126.857z" />
          </g>
          <g>
            <path d="M414.144,81.769c-6.272-6.208-16.416-6.176-22.624,0.096c-6.208,6.272-6.176,16.416,0.096,22.624C427.968,140.553,448,188.681,448,240.009s-20.032,99.456-56.384,135.52c-6.272,6.208-6.304,16.352-0.096,22.624c3.136,3.168,7.232,4.736,11.36,4.736c4.064,0,8.128-1.536,11.264-4.64C456.608,356.137,480,299.945,480,240.009C480,180.073,456.608,123.881,414.144,81.769z" />
          </g>
        </svg>
      );
    }
    return (
      <svg
        className="svg-icon svg-icon--volume-muted"
        aria-hidden="true"
        focusable="false"
        role="presentation"
        xmlns="http://www.w3.org/2000/svg"
        fill="currentColor"
        width="20"
        height="20"
        viewBox="0 0 480 480"
        style={{ outline: "none", stroke: "none" }}
      >
        <g>
          <path d="M278.9,17.6c-5.6-2.7-12.1-2-16.9,1.9L106.4,144H32c-17.6,0-32,14.4-32,32v128c0,17.6,14.4,32,32,32h74.4L262,460.5c2.9,2.3,6.5,3.5,10,3.5c2.4,0,4.7-0.5,6.9-1.6c5.5-2.7,9.1-8.3,9.1-14.4V32C288,25.9,284.5,20.3,278.9,17.6z M96,304H32V176h64V304z M256,414.7L128,312.3V167.7L256,65.3V414.7z" />
        </g>
        <g>
          <path d="M425.2,250l49.4-49.4c5.9-5.9,5.9-15.4,0-21.2c-5.9-5.9-15.4-5.9-21.2,0L404,228.8l-49.4-49.4c-5.9-5.9-15.4-5.9-21.2,0c-5.9,5.9-5.9,15.4,0,21.2l49.4,49.4l-49.4,49.4c-5.9,5.9-5.9,15.4,0,21.2c5.9,5.9,15.4,5.9,21.2,0l49.4-49.4l49.4,49.4c5.9,5.9,15.4,5.9,21.2,0c5.9-5.9,5.9-15.4,0-21.2L425.2,250z" />
        </g>
      </svg>
    );
  };

  // ─── Arrow Button — NO hover effects, NO border, NO movement, NO SVG border ─
  const ArrowButton = ({ direction, visible }) => {
    const isLeft = direction === "left";
    return (
      <button
        className={`arrow-btn-eight ${isLeft ? "arrow-prev-eight" : "arrow-next-eight"} ${
          visible ? "arrow-visible-eight" : "arrow-hidden-eight"
        }`}
        onClick={(e) => {
          e.stopPropagation();
          navigateCarousel(direction);
        }}
        aria-label={isLeft ? "Previous video" : "Next video"}
        tabIndex={visible ? 0 : -1}
      >
        {isLeft ? (
          /* Left chevron - no black border */
          <svg
            className="svg-icon svg-icon--arrow-left"
            aria-hidden="true"
            focusable="false"
            role="presentation"
            xmlns="http://www.w3.org/2000/svg"
            width="26"
            height="26"
            viewBox="0 0 72 72"
            style={{ outline: "none", stroke: "none" }}
          >
            <g transform="translate(-36 -511)">
              <g transform="translate(23289 11206)">
                <g>
                  <rect width="72" height="72" transform="translate(-23253 -10695)" fill="none" style={{ stroke: "none" }} />
                  <path
                    d="M35.628,87.789l.876-.876a1.487,1.487,0,0,0,0-2.1L12.281,60.587,36.5,36.365a1.487,1.487,0,0,0,0-2.1l-.876-.876a1.487,1.487,0,0,0-2.1,0L7.375,59.536a1.487,1.487,0,0,0,0,2.1l26.15,26.15a1.487,1.487,0,0,0,2.1,0Z"
                    transform="translate(-23239.939 -10719.95)"
                    fill="currentColor"
                    style={{ stroke: "none" }}
                  />
                </g>
              </g>
            </g>
          </svg>
        ) : (
          /* Right chevron - no black border */
          <svg
            className="svg-icon svg-icon--arrow-right"
            aria-hidden="true"
            focusable="false"
            role="presentation"
            xmlns="http://www.w3.org/2000/svg"
            width="26"
            height="26"
            viewBox="0 0 72 72"
            style={{ outline: "none", stroke: "none" }}
          >
            <g transform="translate(108 583) rotate(180)">
              <g transform="translate(23289 11206)">
                <g>
                  <rect width="72" height="72" transform="translate(-23253 -10695)" fill="none" style={{ stroke: "none" }} />
                  <path
                    d="M35.628,87.789l.876-.876a1.487,1.487,0,0,0,0-2.1L12.281,60.587,36.5,36.365a1.487,1.487,0,0,0,0-2.1l-.876-.876a1.487,1.487,0,0,0-2.1,0L7.375,59.536a1.487,1.487,0,0,0,0,2.1l26.15,26.15a1.487,1.487,0,0,0,2.1,0Z"
                    transform="translate(-23239.939 -10719.95)"
                    fill="currentColor"
                    style={{ stroke: "none" }}
                  />
                </g>
              </g>
            </g>
          </svg>
        )}
      </button>
    );
  };

  return (
    <>
      <section className="bestscreencare-wrapper-eight">
        <h2 className="bestscreencare-heading-eight">Best Skincare Products</h2>

        <div
          className="carousel-container-eight"
          ref={carouselRef}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          {/* Left arrow — visible only when NOT on first video */}
          <ArrowButton direction="left" visible={showLeft} />

          {/* Right arrow — visible only when NOT on last video */}
          <ArrowButton direction="right" visible={showRight} />

          <div className="bestscreencare-grid-eight" ref={gridRef}>
            {videos.map((item, index) => {
              const isActive = index === activeIndex;
              return (
                <div
                  key={`${item.id}-${index}`}
                  className={`video-container-eight ${isActive ? "active-eight" : ""}`}
                  onClick={() => handleVideoClick(index)}
                  style={{ minWidth: `${cardWidth}px`, width: `${cardWidth}px` }}
                >
                  <div className="video-wrapper-eight">
                    <video
                      ref={(el) => (videoRefs.current[index] = el)}
                      src={item.src}
                      muted
                      loop
                      playsInline
                      preload={isActive ? "auto" : "metadata"}
                      className="bestscreencare-video-eight"
                    />
                    {isActive && (
                      <button
                        className="volume-btn-eight"
                        onClick={toggleMute}
                        aria-label={muted ? "Unmute video" : "Mute video"}
                      >
                        <VolumeIcon muted={muted} />
                      </button>
                    )}
                  </div>

                  {isActive && (
                    <>
                      <div
                        className="shopify-product-card-eight"
                        onClick={(e) => handleProductClick(e, item.id)}
                      >
                        <img
                          src={`${item.image}?width=120`}
                          alt={item.title}
                          loading="lazy"
                        />
                        <div className="card-info-eight">
                          <span className="brand-eight">{item.brand}</span>
                          <h4>{item.title}</h4>
                          <p>{formatPrice(item.price)}</p>
                        </div>
                        <div
                          className="cart-icon-wrapper-eight"
                          onClick={(e) => handleCartClick(e, item)}
                          aria-label="Add to cart"
                          role="button"
                        >
                          <BsBagDash className="cart-icon-eight" />
                        </div>
                      </div>

                      <div className="slider-dots-eight">
                        {originalVideos.map((_, i) => (
                          <span
                            key={`dot-${i}`}
                            className={`dot-eight ${
                              i === activeIndex % originalVideos.length ? "active-eight" : ""
                            }`}
                            onClick={(e) => handleDotClick(e, i)}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {drawerOpen && (
        <div className="quick-drawer-overlay-eight" onClick={handleDrawerClose} />
      )}

      <aside className={`quick-drawer-eight ${drawerOpen ? "open" : ""}`}>
        {drawerItem && (
          <>
            <button
              className="quick-drawer-close-eight"
              onClick={handleDrawerClose}
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                <path
                  fill="none"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M18 6L6 18M6 6l12 12"
                />
              </svg>
            </button>

            <div className="quick-drawer-image-eight">
              <img
                src={drawerItem.image}
                alt={drawerItem.title}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/images/image_not_found 1.svg";
                }}
              />
            </div>

            <div className="quick-drawer-body-eight">
              <p className="quick-drawer-brand-eight">{drawerItem.brand}</p>
              <h3 className="quick-drawer-title-eight">{drawerItem.title}</h3>

              <div className="quick-drawer-price-eight">
                <span>{formatPrice(drawerItem.price)}</span>
              </div>
              <p className="quick-drawer-tax-eight">Tax included.</p>

              <div className="quick-drawer-quantity-eight">
                <button
                  className="qty-btn-eight qty-minus-eight"
                  onClick={handleQtyDecrease}
                  aria-label="Decrease quantity"
                  disabled={drawerQty <= 1}
                >
                  <span>-</span>
                </button>
                <span className="qty-value-eight">{drawerQty}</span>
                <button
                  className="qty-btn-eight qty-plus-eight"
                  onClick={handleQtyIncrease}
                  aria-label="Increase quantity"
                >
                  <span>+</span>
                </button>
              </div>

              <button className="quick-drawer-atc-btn-eight" onClick={handleAddToCart}>
                ADD TO CART
              </button>

              <button className="quick-drawer-view-details-eight" onClick={handleViewDetails}>
                View all details
              </button>
            </div>
          </>
        )}
      </aside>
    </>
  );
};

export default HomeTrendingSection;